import { Filter } from "@sisense/sdk-data";
import {
  SisenseContextProvider,
  WidgetById,
  ThemeProvider,
} from "@sisense/sdk-ui";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { GetDashboardThemeByDashboardId } from "./GetDashboardThemeByDashboardId";
import { CustomerAccountOrNone } from "../ReportBuilderEditor";

export interface SisenseWidgetProps {
  sisenseEnv: string;
  onAfterRender: (
    divNode: HTMLDivElement | undefined,
    error: Error | undefined
  ) => void;
  widgetOid: string;
  dashboardOid: string;
  customerAccount: CustomerAccountOrNone;
  filters: Filter[];
  height: number;
  width: number;
}

export const SisenseWidget: FunctionComponent<SisenseWidgetProps> = ({
  sisenseEnv,
  onAfterRender,
  widgetOid,
  dashboardOid,
  customerAccount,
  filters,
  height,
  width,
}) => {
  const [dashboardTheme, setDashboardTheme] = useState<any>(null);
  const [isThemeFetched, setIsThemeFetched] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Only observe after widget is rendered and theme is fetched
  useEffect(() => {
    if (!isThemeFetched) return;
    const ref = containerRef.current;
    if (!ref) return;
    let onAfterRenderCalled = false;
    const observer = new MutationObserver(() => {
      if (onAfterRenderCalled) return;
      const chartReady = ref.querySelector(".highcharts-root");
      const noResultsDiv = Array.from(ref.querySelectorAll("div") || []).find(
        (div) => div.textContent?.includes("No Results")
      );
      if (chartReady || noResultsDiv) {
        onAfterRenderCalled = true;
        observer.disconnect();
        // Add a small delay to ensure full paint
        setTimeout(() => {
          onAfterRender(ref, undefined);
        }, 850);
      }
    });
    observer.observe(ref, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isThemeFetched, onAfterRender]);

  const handleError = (error: Error) => {
    onAfterRender(containerRef.current || undefined, error);
  };

  const handleTheme = (theme?: any | Error) => {
    if (theme instanceof Error) {
      setDashboardTheme({ name: "None" });
      setIsThemeFetched(true);
    } else {
      setDashboardTheme(theme);
      setIsThemeFetched(true);
    }
  };

  // Helper to apply yAxis stack label formatter override
  function applyYAxisStackLabelFormatter(options: any) {
    const existingAxisFormatterWithCorrectConfiguration =
      options.yAxis?.[0]?.labels?.formatter;
    if (
      options.yAxis?.[0]?.stackLabels?.formatter &&
      existingAxisFormatterWithCorrectConfiguration
    ) {
      options.yAxis[0].stackLabels.formatter = function (this: any) {
        return existingAxisFormatterWithCorrectConfiguration.call({
          ...this,
          value: this.total,
        });
      };
    }
  }

  return (
    <SisenseContextProvider
      url={sisenseEnv}
      ssoEnabled={true}
      showRuntimeErrors={false}
      onError={handleError}
    >
      {!isThemeFetched && (
        <GetDashboardThemeByDashboardId
          dashboardOid={dashboardOid}
          exitComponent={handleTheme}
        />
      )}
      {isThemeFetched && (
        <div
          ref={containerRef}
          style={{ height: `${height}px`, width: `${width}px` }}
        >
          {dashboardTheme && (
            <ThemeProvider theme={dashboardTheme}>
              <WidgetById
                dashboardOid={dashboardOid}
                widgetOid={widgetOid}
                filters={filters}
                styleOptions={{ height, width }}
                onBeforeRender={(options: any) => {
                  if (customerAccount && customerAccount.name === "Atkins") {
                    if (options.xAxis) {
                      const xAxes = Array.isArray(options.xAxis)
                        ? options.xAxis
                        : [options.xAxis];
                      xAxes.forEach((xAxis: any) => {
                        if (xAxis.labels) {
                          xAxis.labels.style.fontFamily = "Arial";
                          xAxis.labels.style.fontSize = "14pt";
                        }
                      });
                    }
                    if (options.yAxis) {
                      const yAxes = Array.isArray(options.yAxis)
                        ? options.yAxis
                        : [options.yAxis];
                      yAxes.forEach((yAxis: any) => {
                        if (yAxis.labels) {
                          yAxis.labels.style.fontFamily = "Arial";
                          yAxis.labels.style.fontSize = "14pt";
                        }
                      });
                    }
                    if (options.legend) {
                      options.legend.itemStyle.fontFamily = "Arial";
                      options.legend.itemStyle.fontSize = "14pt";
                    }
                    if (options.plotOptions) {
                      if (
                        options.plotOptions.series &&
                        options.plotOptions.series.dataLabels
                      ) {
                        options.plotOptions.series.dataLabels.style.fontFamily =
                          "Arial";
                        options.plotOptions.series.dataLabels.style.fontSize =
                          "14pt";
                      }
                      if (options.plotOptions.pie) {
                        options.plotOptions.pie.dataLabels.style.fontFamily =
                          "Arial";
                        options.plotOptions.pie.dataLabels.style.fontSize =
                          "14pt";
                      }
                    }
                    applyYAxisStackLabelFormatter(options);
                    return options;
                  }
                  applyYAxisStackLabelFormatter(options);
                  return options;
                }}
              />
            </ThemeProvider>
          )}
          {!dashboardTheme && (
            <WidgetById
              dashboardOid={dashboardOid}
              widgetOid={widgetOid}
              filters={filters}
              styleOptions={{ height, width }}
              onBeforeRender={(options: any) => {
                applyYAxisStackLabelFormatter(options);
                return options;
              }}
            />
          )}
        </div>
      )}
    </SisenseContextProvider>
  );
};
